import { useMemo } from 'react';
import { getParkByCode } from '@havenengineering/module-shared-library/dist/utils/parks/getters';
import { HolidayType } from '@havenengineering/module-shared-library/dist/utils/parks/types';
import { useRouter } from 'next/router';
import { Query } from '../types/v2/holiday-search';
import { getFilterWhenSelection } from '../helpers/dates';
import { HIDEAWAY_PACKAGE } from '../constant';
import { getPreviousSteps, getStep } from '../helpers/redirects';
import { useJourneySteps } from './useJourneySteps';

const useStepSelections = (query: Query) => {
  const router = useRouter();

  const stepSelectionsUnordered = getStepSelectionsUnordered(query);

  const journeySteps = useJourneySteps(undefined, query);

  const currentStep = getStep(journeySteps, router.pathname);

  const previousSteps = getPreviousSteps(journeySteps, currentStep);

  const orderedStepsWithSelections = useMemo(() => {
    return journeySteps.map((newJourneyStep) => {
      const matchingStep = stepSelectionsUnordered
        ?.filter((selection) =>
          previousSteps.find((previousStep) => selection.name === previousStep.name),
        )
        ?.find((step) => step.name === newJourneyStep.name);

      return {
        ...newJourneyStep,
        ...matchingStep,
      };
    });
  }, [query, stepSelectionsUnordered]);

  return { stepsWithSelections: orderedStepsWithSelections };
};

export const getStepSelectionsUnordered = (query: Query) => {
  const getPackageTypeName = (packageType: string) =>
    packageType === HIDEAWAY_PACKAGE ? 'Haven Hideaway' : 'Haven';

  return [
    {
      name: 'Date',
      selection: (query?.arrivalDate && getFilterWhenSelection(query, false, true)) || '',
      icon: 'vertical-nav-date.svg',
    },
    {
      name: 'Park',
      selection: query?.parkCode
        ? getParkByCode(HolidayType.HOLIDAY, query?.parkCode || '')?.name
        : '',
      icon: 'vertical-nav-location.svg',
    },
    {
      name: 'Accommodation',
      selection: query?.selectedGradeName || '',
      icon: 'vertical-nav-van.svg',
    },
    {
      name: 'Bedroom',
      selection: query?.selectedBeds ? `${query?.selectedBeds} beds` : '',
      icon: 'double-bed-darkblue.svg',
    },
    {
      name: 'Package',
      selection: (query?.packageType && getPackageTypeName(query.packageType)) || '',
      icon: 'vertical-nav-package.svg',
    },
  ];
};

export default useStepSelections;
